import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  FileText, Download, Mail, Trash2, Settings, Plus, X,
  Users, Calculator, Building2, Palmtree, Briefcase, MapPin, CreditCard,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const MONTHS_FR = [
  'Janvier','Fevrier','Mars','Avril','Mai','Juin',
  'Juillet','Aout','Septembre','Octobre','Novembre','Decembre'
];

const formatCHF = (v) => `CHF ${(v || 0).toLocaleString('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MARITAL_LABELS = {
  celibataire: 'Celibataire', marie: 'Marie(e)', divorce: 'Divorce(e)',
  veuf: 'Veuf/Veuve', partenariat: 'Partenariat',
};

export default function PayslipPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState('generate');
  const [employees, setEmployees] = useState([]);
  const [selectedEmpId, setSelectedEmpId] = useState('');
  const [empInfo, setEmpInfo] = useState(null);
  const [month, setMonth] = useState(() => {
    const n = new Date();
    return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}`;
  });
  const [salaryConfig, setSalaryConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [payslips, setPayslips] = useState([]);
  const [allPayslips, setAllPayslips] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [configDirty, setConfigDirty] = useState(false);
  const [detailPayslip, setDetailPayslip] = useState(null);
  const [defaultsDirty, setDefaultsDirty] = useState(false);

  const loadEmployees = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees((res.data || []).filter(e => e.is_active));
    } catch { /* */ }
  }, []);

  const loadDefaults = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payslip/defaults`);
      setDefaults(res.data);
    } catch { /* */ }
  }, []);

  const loadConfig = useCallback(async (eid) => {
    if (!eid) return;
    try {
      const res = await axios.get(`${API}/payslip/config/${eid}`);
      setSalaryConfig(res.data);
      setConfigDirty(false);
    } catch { /* */ }
  }, []);

  const loadEmpInfo = useCallback(async (eid) => {
    if (!eid) return;
    try {
      const res = await axios.get(`${API}/payslip/employee-info/${eid}`);
      setEmpInfo(res.data);
    } catch { /* */ }
  }, []);

  const loadPayslips = useCallback(async (eid) => {
    if (!eid) return;
    try {
      const res = await axios.get(`${API}/payslip/list/${eid}`);
      setPayslips(res.data || []);
    } catch { /* */ }
  }, []);

  const loadAllPayslips = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/payslip/all`);
      setAllPayslips(res.data || []);
    } catch { /* */ }
  }, []);

  useEffect(() => { loadEmployees(); loadDefaults(); loadAllPayslips(); }, [loadEmployees, loadDefaults, loadAllPayslips]);

  useEffect(() => {
    if (selectedEmpId) {
      loadConfig(selectedEmpId);
      loadPayslips(selectedEmpId);
      loadEmpInfo(selectedEmpId);
    } else {
      setEmpInfo(null);
      setSalaryConfig(null);
    }
  }, [selectedEmpId, loadConfig, loadPayslips, loadEmpInfo]);

  const saveConfig = async () => {
    if (!selectedEmpId || !salaryConfig) return;
    try {
      const res = await axios.put(`${API}/payslip/config/${selectedEmpId}`, salaryConfig);
      setSalaryConfig(res.data);
      setConfigDirty(false);
      toast.success('Configuration salariale sauvegardee');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const saveDefaults = async () => {
    if (!defaults) return;
    try {
      const res = await axios.put(`${API}/payslip/defaults`, defaults);
      setDefaults(res.data);
      setDefaultsDirty(false);
      toast.success('Configuration entreprise sauvegardee');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const generatePayslip = async () => {
    if (!selectedEmpId || !month) return;
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/payslip/generate`, { employee_id: selectedEmpId, month });
      toast.success(`Fiche generee: ${formatCHF(res.data.net_salary)} net`);
      setDetailPayslip(res.data);
      loadPayslips(selectedEmpId);
      loadAllPayslips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur generation');
    } finally {
      setGenerating(false);
    }
  };

  const downloadPdf = async (psId) => {
    try {
      const res = await axios.get(`${API}/payslip/${psId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      // Open in new tab for viewing
      window.open(url, '_blank');
      // Also trigger download
      setTimeout(() => {
        const a = document.createElement('a');
        a.href = url;
        a.download = `fiche_salaire_${psId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        setTimeout(() => window.URL.revokeObjectURL(url), 5000);
      }, 500);
    } catch (e) {
      console.error('PDF download error:', e);
      toast.error('Erreur telechargement PDF');
    }
  };

  const emailPayslip = async (psId) => {
    try {
      const res = await axios.post(`${API}/payslip/${psId}/email`);
      toast.success(res.data.message);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi email');
    }
  };

  const deletePayslip = async (psId) => {
    if (!window.confirm('Supprimer cette fiche de salaire ?')) return;
    try {
      await axios.delete(`${API}/payslip/${psId}`);
      toast.success('Fiche supprimee');
      loadPayslips(selectedEmpId);
      loadAllPayslips();
      if (detailPayslip?.id === psId) setDetailPayslip(null);
    } catch {
      toast.error('Erreur suppression');
    }
  };

  const updateConfig = (key, val) => {
    setSalaryConfig(p => ({ ...p, [key]: val }));
    setConfigDirty(true);
  };

  const updateDefaults = (key, val) => {
    setDefaults(p => ({ ...p, [key]: val }));
    setDefaultsDirty(true);
  };

  const canGenerate = selectedEmpId && month && salaryConfig && (
    (salaryConfig.salary_type === 'hourly' && salaryConfig.hourly_rate > 0) ||
    (salaryConfig.salary_type !== 'hourly' && salaryConfig.monthly_gross > 0)
  );

  const tabs = [
    { id: 'generate', label: 'Generer', icon: FileText },
    { id: 'history', label: 'Historique', icon: Calculator },
    { id: 'config', label: 'Configuration', icon: Settings },
  ];

  return (
    <div className="space-y-4" data-testid="payslip-page">
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit" data-testid="payslip-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium transition-all ${
              tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Employee selector */}
      {tab !== 'config' && (
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-slate-500 mb-1 block">Employe</Label>
              <Select value={selectedEmpId} onValueChange={setSelectedEmpId}>
                <SelectTrigger data-testid="employee-select">
                  <SelectValue placeholder="Selectionner un employe" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.first_name} {e.last_name} — {e.role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {tab === 'generate' && (
              <>
                <div className="w-44">
                  <Label className="text-xs text-slate-500 mb-1 block">{t('common.month')}</Label>
                  <Input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="month-input" />
                </div>
                <Button
                  onClick={generatePayslip}
                  disabled={!canGenerate || generating}
                  data-testid="generate-btn"
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {generating ? 'Generation...' : 'Generer la fiche'}
                </Button>
              </>
            )}
          </div>
          {tab === 'generate' && selectedEmpId && salaryConfig && !canGenerate && (
            <p className="text-amber-600 text-sm mt-2">
              Configurez d'abord le salaire ({salaryConfig.salary_type === 'hourly' ? 'taux horaire' : 'salaire mensuel'}) dans l'onglet Configuration.
            </p>
          )}
        </Card>
      )}

      {/* Employee info card (auto-fill from DB) */}
      {tab === 'generate' && empInfo && (
        <Card className="p-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <InfoItem icon={Briefcase} label="Fonction" value={empInfo.position || empInfo.role} />
            <InfoItem icon={MapPin} label="Adresse" value={[empInfo.address_street, `${empInfo.address_postal_code} ${empInfo.address_city}`.trim()].filter(Boolean).join(', ')} />
            <InfoItem icon={CreditCard} label="AVS" value={empInfo.avs_number} />
            <InfoItem icon={CreditCard} label="IBAN" value={empInfo.bank_iban} />
          </div>
          {empInfo.vacation && (
            <div className="mt-3 flex items-center gap-4 p-2.5 bg-emerald-50 rounded-lg">
              <Palmtree className="w-4 h-4 text-emerald-600" />
              <span className="text-sm text-emerald-800">
                Vacances: <b>{empInfo.vacation.entitled}j</b> droit | <b>{empInfo.vacation.taken}j</b> pris | <b className="text-emerald-700">{empInfo.vacation.remaining}j</b> restant
              </span>
            </div>
          )}
        </Card>
      )}

      {/* ═══ GENERATE TAB ═══ */}
      {tab === 'generate' && detailPayslip && (
        <PayslipDetail ps={detailPayslip} onDownload={downloadPdf} onEmail={emailPayslip} />
      )}

      {tab === 'generate' && selectedEmpId && payslips.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold text-sm text-slate-700 mb-3">Dernieres fiches</h3>
          <div className="space-y-2">
            {payslips.slice(0, 3).map(ps => (
              <PayslipRow key={ps.id} ps={ps} onView={setDetailPayslip} onDownload={downloadPdf} onEmail={emailPayslip} onDelete={deletePayslip} />
            ))}
          </div>
        </Card>
      )}

      {/* ═══ HISTORY TAB ═══ */}
      {tab === 'history' && (
        <Card className="p-4">
          <h3 className="font-semibold text-sm text-slate-700 mb-3">
            {selectedEmpId ? `Historique — ${empInfo?.first_name} ${empInfo?.last_name}` : 'Toutes les fiches'}
          </h3>
          {(selectedEmpId ? payslips : allPayslips).length === 0 ? (
            <p className="text-slate-400 text-sm py-6 text-center">Aucune fiche de salaire</p>
          ) : (
            <div className="space-y-2">
              {(selectedEmpId ? payslips : allPayslips).map(ps => (
                <PayslipRow key={ps.id} ps={ps} onView={setDetailPayslip} onDownload={downloadPdf} onEmail={emailPayslip} onDelete={deletePayslip} showName={!selectedEmpId} />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* ═══ CONFIG TAB ═══ */}
      {tab === 'config' && (
        <div className="grid lg:grid-cols-2 gap-4">
          {/* Company config */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-800">Entreprise & Taux par defaut</h3>
            </div>
            {defaults && (
              <div className="space-y-3">
                <ConfigInput label="Nom de l'entreprise" value={defaults.company_name || ''} onChange={v => updateDefaults('company_name', v)} testid="def-company-name" />
                <ConfigInput label="Adresse" value={defaults.company_address || ''} onChange={v => updateDefaults('company_address', v)} testid="def-company-addr" />
                <ConfigInput label="No IDE" value={defaults.company_ide || ''} onChange={v => updateDefaults('company_ide', v)} testid="def-company-ide" />
                <div className="border-t pt-3 mt-3">
                  <p className="text-xs font-semibold text-slate-500 mb-2">Cotisations sociales (%)</p>
                  <div className="grid grid-cols-2 gap-2">
                    <ConfigInput label="AVS/AI/APG employe" value={defaults.avs_ai_apg_employee} onChange={v => updateDefaults('avs_ai_apg_employee', parseFloat(v) || 0)} type="number" testid="def-avs-emp" />
                    <ConfigInput label="AVS/AI/APG employeur" value={defaults.avs_ai_apg_employer} onChange={v => updateDefaults('avs_ai_apg_employer', parseFloat(v) || 0)} type="number" testid="def-avs-er" />
                    <ConfigInput label="AC employe" value={defaults.ac_employee} onChange={v => updateDefaults('ac_employee', parseFloat(v) || 0)} type="number" testid="def-ac-emp" />
                    <ConfigInput label="AC employeur" value={defaults.ac_employer} onChange={v => updateDefaults('ac_employer', parseFloat(v) || 0)} type="number" testid="def-ac-er" />
                    <ConfigInput label="AANP employe" value={defaults.aanp_employee} onChange={v => updateDefaults('aanp_employee', parseFloat(v) || 0)} type="number" testid="def-aanp" />
                    <ConfigInput label="LPP employe" value={defaults.lpp_employee} onChange={v => updateDefaults('lpp_employee', parseFloat(v) || 0)} type="number" testid="def-lpp-emp" />
                    <ConfigInput label="LPP employeur" value={defaults.lpp_employer} onChange={v => updateDefaults('lpp_employer', parseFloat(v) || 0)} type="number" testid="def-lpp-er" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <ConfigInput label="Deduction LPP coord." value={defaults.lpp_coordination_deduction} onChange={v => updateDefaults('lpp_coordination_deduction', parseFloat(v) || 0)} type="number" testid="def-lpp-coord" />
                    <ConfigInput label="Seuil entree LPP" value={defaults.lpp_entry_threshold} onChange={v => updateDefaults('lpp_entry_threshold', parseFloat(v) || 0)} type="number" testid="def-lpp-thresh" />
                  </div>
                </div>
                <Button onClick={saveDefaults} disabled={!defaultsDirty} className="w-full mt-2" data-testid="save-defaults-btn">
                  Sauvegarder configuration entreprise
                </Button>
              </div>
            )}
          </Card>

          {/* Employee salary config */}
          <Card className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users className="w-5 h-5 text-slate-600" />
              <h3 className="font-semibold text-slate-800">Salaire employe</h3>
            </div>
            <div className="mb-3">
              <Select value={selectedEmpId} onValueChange={setSelectedEmpId}>
                <SelectTrigger data-testid="config-employee-select">
                  <SelectValue placeholder="Selectionner un employe" />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Employee info summary */}
            {empInfo && (
              <div className="p-3 bg-slate-50 rounded-lg mb-3 text-xs text-slate-600 space-y-1">
                <p><b>{empInfo.first_name} {empInfo.last_name}</b> — {empInfo.position || empInfo.role}</p>
                {empInfo.address_street && <p>{empInfo.address_street}, {empInfo.address_postal_code} {empInfo.address_city}</p>}
                {empInfo.avs_number && <p>AVS: {empInfo.avs_number}</p>}
                {empInfo.bank_iban && <p>IBAN: {empInfo.bank_iban}</p>}
                {empInfo.marital_status && <p>Etat civil: {MARITAL_LABELS[empInfo.marital_status] || empInfo.marital_status}</p>}
              </div>
            )}

            {selectedEmpId && salaryConfig && (
              <div className="space-y-3">
                {/* Salary type toggle */}
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Type de salaire</Label>
                  <div className="flex gap-2">
                    {['monthly', 'hourly'].map(t => (
                      <button
                        key={t}
                        onClick={() => updateConfig('salary_type', t)}
                        data-testid={`salary-type-${t}`}
                        className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border transition-all ${
                          salaryConfig.salary_type === t
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                        }`}
                      >
                        {t === 'monthly' ? 'Salaire mensuel' : 'Salaire horaire'}
                      </button>
                    ))}
                  </div>
                </div>

                {salaryConfig.salary_type === 'hourly' ? (
                  <ConfigInput label="Taux horaire (CHF)" value={salaryConfig.hourly_rate || ''} onChange={v => updateConfig('hourly_rate', parseFloat(v) || 0)} type="number" testid="cfg-hourly-rate" />
                ) : (
                  <ConfigInput label="Salaire mensuel brut (CHF)" value={salaryConfig.monthly_gross || ''} onChange={v => updateConfig('monthly_gross', parseFloat(v) || 0)} type="number" testid="cfg-monthly-gross" />
                )}

                <ConfigInput label="Taux d'occupation (%)" value={salaryConfig.occupation_rate || 100} onChange={v => updateConfig('occupation_rate', parseFloat(v) || 100)} type="number" testid="cfg-occ-rate" />
                <ConfigInput label="Jours de vacances / an" value={salaryConfig.vacation_days_per_year || 20} onChange={v => updateConfig('vacation_days_per_year', parseInt(v) || 20)} type="number" testid="cfg-vacation-days" />

                <div className="flex items-center gap-2">
                  <Switch checked={salaryConfig.thirteenth_salary !== false} onCheckedChange={v => updateConfig('thirteenth_salary', v)} data-testid="cfg-13th" />
                  <Label className="text-sm">13e salaire</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch checked={!!salaryConfig.is_source_tax} onCheckedChange={v => updateConfig('is_source_tax', v)} data-testid="cfg-source-tax-toggle" />
                  <Label className="text-sm">Impot a la source</Label>
                </div>
                {salaryConfig.is_source_tax && (
                  <ConfigInput label="Taux impot source (%)" value={salaryConfig.source_tax_rate || ''} onChange={v => updateConfig('source_tax_rate', parseFloat(v) || 0)} type="number" testid="cfg-source-tax-rate" />
                )}

                {/* Allocations */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-500">Allocations / Indemnites</p>
                    <button onClick={() => updateConfig('allowances', [...(salaryConfig.allowances || []), { label: '', amount: 0 }])} className="text-xs text-emerald-600 hover:text-emerald-700 flex items-center gap-1" data-testid="add-allowance-btn">
                      <Plus className="w-3 h-3" /> Ajouter
                    </button>
                  </div>
                  {(salaryConfig.allowances || []).map((a, i) => (
                    <div key={i} className="flex gap-2 mb-1">
                      <Input placeholder="Libelle" value={a.label} onChange={e => { const arr = [...(salaryConfig.allowances || [])]; arr[i] = { ...arr[i], label: e.target.value }; updateConfig('allowances', arr); }} className="text-sm h-8" />
                      <Input type="number" placeholder="CHF" value={a.amount} onChange={e => { const arr = [...(salaryConfig.allowances || [])]; arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 }; updateConfig('allowances', arr); }} className="text-sm h-8 w-28" />
                      <button onClick={() => updateConfig('allowances', (salaryConfig.allowances || []).filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>

                {/* Extra deductions */}
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-500">Deductions supplementaires</p>
                    <button onClick={() => updateConfig('deductions', [...(salaryConfig.deductions || []), { label: '', amount: 0 }])} className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1" data-testid="add-deduction-btn">
                      <Plus className="w-3 h-3" /> Ajouter
                    </button>
                  </div>
                  {(salaryConfig.deductions || []).map((d, i) => (
                    <div key={i} className="flex gap-2 mb-1">
                      <Input placeholder="Libelle" value={d.label} onChange={e => { const arr = [...(salaryConfig.deductions || [])]; arr[i] = { ...arr[i], label: e.target.value }; updateConfig('deductions', arr); }} className="text-sm h-8" />
                      <Input type="number" placeholder="CHF" value={d.amount} onChange={e => { const arr = [...(salaryConfig.deductions || [])]; arr[i] = { ...arr[i], amount: parseFloat(e.target.value) || 0 }; updateConfig('deductions', arr); }} className="text-sm h-8 w-28" />
                      <button onClick={() => updateConfig('deductions', (salaryConfig.deductions || []).filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>

                <Button onClick={saveConfig} disabled={!configDirty} className="w-full mt-2" data-testid="save-config-btn">
                  Sauvegarder configuration employe
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Detail dialog for history tab */}
      {detailPayslip && tab === 'history' && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetailPayslip(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-5" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-3">
              <h3 className="font-semibold text-lg">Fiche de salaire</h3>
              <button onClick={() => setDetailPayslip(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <PayslipDetail ps={detailPayslip} onDownload={downloadPdf} onEmail={emailPayslip} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Sub-components ─── */

function InfoItem({ icon: Icon, label, value }) {
  const { t } = useTranslation();
  if (!value) return null;
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
      <div>
        <p className="text-[10px] text-slate-400 uppercase">{label}</p>
        <p className="text-slate-700 text-sm">{value}</p>
      </div>
    </div>
  );
}

function ConfigInput({ label, value, onChange, type = 'text', testid }) {
  const { t } = useTranslation();
  return (
    <div>
      <Label className="text-xs text-slate-500">{label}</Label>
      <Input type={type} value={value} onChange={e => onChange(e.target.value)} className="h-8 text-sm" step={type === 'number' ? '0.01' : undefined} data-testid={testid} />
    </div>
  );
}

function PayslipRow({ ps, onView, onDownload, onEmail, onDelete, showName = false }) {
  const { t } = useTranslation();
  const [y, m] = (ps.month || '').split('-');
  const label = `${MONTHS_FR[parseInt(m) - 1] || m} ${y}`;
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer" onClick={() => onView(ps)} data-testid={`payslip-row-${ps.id}`}>
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
          <FileText className="w-4 h-4 text-emerald-700" />
        </div>
        <div>
          <p className="text-sm font-medium text-slate-800">
            {showName && <span className="text-slate-600">{ps.employee_name} — </span>}
            {label}
            {ps.salary_type === 'hourly' && <span className="text-xs text-slate-400 ml-1">(horaire)</span>}
          </p>
          <p className="text-xs text-slate-500">
            Brut: {formatCHF(ps.gross_salary)} | Net: <span className="font-semibold text-emerald-700">{formatCHF(ps.net_salary)}</span>
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDownload(ps.id)} data-testid={`download-pdf-${ps.id}`}><Download className="w-4 h-4 text-slate-500" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEmail(ps.id)} data-testid={`email-btn-${ps.id}`}><Mail className="w-4 h-4 text-blue-500" /></Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onDelete(ps.id)} data-testid={`delete-btn-${ps.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>
      </div>
    </div>
  );
}

function PayslipDetail({ ps, onDownload, onEmail }) {
  const ded = ps.deductions || {};
  const ec = ps.employer_contributions || {};
  const vac = ps.vacation || {};
  return (
    <Card className="p-5 space-y-4" data-testid="payslip-detail">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-slate-800">{ps.employee_name} — {ps.month_name}</h3>
          <p className="text-xs text-slate-400">{ps.salary_type === 'hourly' ? 'Salaire horaire' : 'Salaire mensuel'}</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => onDownload(ps.id)} data-testid="detail-download-btn"><Download className="w-4 h-4 mr-1" /> PDF</Button>
          <Button size="sm" variant="outline" onClick={() => onEmail(ps.id)} data-testid="detail-email-btn"><Mail className="w-4 h-4 mr-1" /> Envoyer</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
        <div className="bg-slate-50 p-3 rounded-lg">
          <p className="text-xs text-slate-500">Taux</p>
          <p className="font-semibold">{ps.occupation_rate}%</p>
        </div>
        <div className="bg-slate-50 p-3 rounded-lg">
          <p className="text-xs text-slate-500">Jours ouvr.</p>
          <p className="font-semibold">{ps.working_days}</p>
        </div>
        <div className="bg-slate-50 p-3 rounded-lg">
          <p className="text-xs text-slate-500">Heures</p>
          <p className="font-semibold">{ps.hours_worked}h</p>
        </div>
        {ps.salary_type === 'hourly' && (
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-xs text-slate-500">Taux horaire</p>
            <p className="font-semibold">{formatCHF(ps.hourly_rate)}</p>
          </div>
        )}
        {ps.salary_type !== 'hourly' && ps.expected_hours > 0 && (
          <div className="bg-slate-50 p-3 rounded-lg">
            <p className="text-xs text-slate-500">Heures attendues</p>
            <p className="font-semibold">{ps.expected_hours}h</p>
          </div>
        )}
        {ps.salary_type !== 'hourly' && ps.overtime_hours > 0 && (
          <div className="bg-amber-50 p-3 rounded-lg border border-amber-200">
            <p className="text-xs text-amber-600">Heures sup.</p>
            <p className="font-semibold text-amber-700">{ps.overtime_hours}h</p>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2">SALAIRE BRUT</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span>{ps.salary_type === 'hourly' ? `${ps.hours_worked}h x ${formatCHF(ps.hourly_rate)}` : `Salaire mensuel (${ps.occupation_rate}%)`}</span>
              <span className="font-medium">{formatCHF(ps.gross_salary)}</span>
            </div>
            {ps.salary_type !== 'hourly' && ps.overtime_hours > 0 && (
              <div className="flex justify-between text-amber-700">
                <span>Heures sup. ({ps.overtime_hours}h x 125%)</span>
                <span>+{formatCHF(ps.overtime_amount)}</span>
              </div>
            )}
            {(ps.allowances || []).map((a, i) => (
              <div key={i} className="flex justify-between text-emerald-700"><span>{a.label}</span><span>+{formatCHF(a.amount)}</span></div>
            ))}
            <div className="flex justify-between font-semibold border-t pt-1"><span>Total brut</span><span>{formatCHF(ps.gross_salary + (ps.overtime_amount || 0) + (ps.total_allowances || 0))}</span></div>
          </div>
        </div>
        <div>
          <p className="text-xs font-semibold text-slate-500 mb-2">DEDUCTIONS</p>
          <div className="space-y-1 text-sm">
            {[['avs_ai_apg', 'AVS/AI/APG'], ['ac', 'AC'], ['aanp', 'AANP'], ['lpp', 'LPP'], ['source_tax', 'Impot source']].map(([k, l]) => {
              const d = ded[k];
              return d?.amount > 0 ? (<div key={k} className="flex justify-between"><span>{l} ({d.rate}%)</span><span className="text-red-600">-{formatCHF(d.amount)}</span></div>) : null;
            })}
            {(ded.extra || []).map((d, i) => (
              <div key={i} className="flex justify-between"><span>{d.label}</span><span className="text-red-600">-{formatCHF(d.amount)}</span></div>
            ))}
            <div className="flex justify-between font-semibold border-t pt-1 text-red-700"><span>Total deductions</span><span>-{formatCHF(ps.total_deductions)}</span></div>
          </div>
        </div>
      </div>

      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 flex items-center justify-between">
        <span className="font-bold text-emerald-900 text-lg">SALAIRE NET</span>
        <span className="font-bold text-emerald-700 text-xl" data-testid="net-salary">{formatCHF(ps.net_salary)}</span>
      </div>

      {/* Vacation balance */}
      {vac.entitled > 0 && (
        <div className="flex items-center gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
          <Palmtree className="w-5 h-5 text-green-600" />
          <div className="text-sm">
            <span className="text-green-800">Solde vacances: </span>
            <b>{vac.entitled}j</b> droit | <b>{vac.taken}j</b> pris | <b className="text-green-700">{vac.remaining}j restant</b>
          </div>
        </div>
      )}

      {ec.total > 0 && (
        <div className="text-xs text-slate-400">
          Charges patronales: AVS {formatCHF(ec.avs_ai_apg)} + AC {formatCHF(ec.ac)} + LPP {formatCHF(ec.lpp)} = <b>{formatCHF(ec.total)}</b>
        </div>
      )}
    </Card>
  );
}
